import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import "./adminEvent.css";

import AdminEventForm from "./AdminEventForm";

/**
 * IMPORTANT: import EVERYTHING for debug
 */
import * as AdminEventService from "./adminEventService";

import {
 fetchAllEvents,
 createEvent,
 updateEvent,
 deleteEvent,
 cancelEvent,
 rescheduleEvent,
 generateZoomLink,
 fetchEventUsers
} from "./adminEventService";


const AdminEventPage = () => {

 const navigate = useNavigate();

 const [events, setEvents] = useState([]);
 const [editing, setEditing] = useState(null);
 const [showForm, setShowForm] = useState(false);
 const [loading, setLoading] = useState(false);
 const [analytics, setAnalytics] = useState({
 totalEvents: 0,
 totalBookings: 0,
 totalRevenue: 0,
 avgOccupancy: 0
 });


 /**
 * ================= DATE HANDLING HELPERS (ADDED)
 */

 const formatDateSafe = (dateValue) => {

 try {

 if (!dateValue) return "-";

 const date = new Date(dateValue);

 if (isNaN(date.getTime())) return dateValue;

 return date.toLocaleDateString("en-IN", {
 day: "2-digit",
 month: "short",
 year: "numeric"
 });

 } catch (error) {

 return dateValue;

 }

 };


const formatTimeSafe = (timeValue) => {

 try {

 if (!timeValue) return "-";

 // Parse UTC time
 const utcDate = new Date(timeValue);

 if (isNaN(utcDate.getTime())) return timeValue;

 // Convert to IST explicitly
 const istString = utcDate.toLocaleString("en-IN", {
 timeZone: "Asia/Kolkata",
 day: "2-digit",
 month: "short",
 year: "numeric",
 hour: "2-digit",
 minute: "2-digit",
 hour12: true
 });

 // Force uppercase AM/PM
 const formatted = istString
 .replace("am", "AM")
 .replace("pm", "PM");

 return `${formatted} IST`;

 } catch (error) {

 return timeValue;

 }

};


 /**
 * CALCULATE ANALYTICS
 */
 const calculateAnalytics = (eventsData) => {
 const totalEvents = eventsData.length;
 const totalBookings = eventsData.reduce((sum, e) => sum + (e.currentBookings || 0), 0);
 const totalRevenue = eventsData.reduce((sum, e) => sum + ((e.currentBookings || 0) * (e.price || 0)), 0);
 const totalCapacity = eventsData.reduce((sum, e) => sum + (e.maxParticipants || 0), 0);
 const avgOccupancy = totalCapacity > 0 ? (totalBookings / totalCapacity * 100) : 0;

 setAnalytics({
 totalEvents,
 totalBookings,
 totalRevenue,
 avgOccupancy: avgOccupancy.toFixed(1)
 });
 };

 /**
 * JOIN ZOOM MEETING
 */
const handleJoinZoom = (event) => {

 try {

 if (!event.zoomLink) {

 alert("Zoom link not available");
 return;

 }

 window.open(event.zoomLink, "_blank", "noopener,noreferrer");

 } catch (error) {

 alert("Failed to open Zoom link");

 }

};


 /**
 * DEBUG: Check what is actually imported
 */
 useEffect(() => {

 loadEvents();

 }, []);



 /**
 * LOAD EVENTS
 */
 const loadEvents = async () => {

 try {

 setLoading(true);

 const safeFetchAllEvents =
 fetchAllEvents ||
 AdminEventService.fetchAllEvents ||
 AdminEventService.fetchAllEvents;

 if (!safeFetchAllEvents) {

 alert("fetchAllEvents is undefined. Check service import path.");

 setLoading(false);

 return;

 }

 const response = await safeFetchAllEvents();

 if (response.status === 200) {

 setEvents(response.data);
 calculateAnalytics(response.data);

 } else {

 alert(response.message || "Failed to load events");

 }

 } catch (error) {

 alert("Unexpected error loading events");

 } finally {

 setLoading(false);

 }

 };



 /**
 * DELETE EVENT
 */
 const handleDelete = async (id) => {

 try {

 if (!window.confirm("Delete this event?")) return;

 const safeDeleteEvent =
 deleteEvent ||
 AdminEventService.deleteEvent ||
 AdminEventService.deleteEvent;

 const response = await safeDeleteEvent(id);

 if (response.status === 200 || response.status === 204) {

 loadEvents();

 } else {

 alert(response.message);

 }

 } catch (error) {

 }

 };



 /**
 * CANCEL EVENT
 */
 const handleCancel = async (id) => {

 try {

 if (!window.confirm("Cancel this event?")) return;

 const safeCancelEvent =
 cancelEvent ||
 AdminEventService.cancelEvent ||
 AdminEventService.cancelEvent;

 const response = await safeCancelEvent(id);

 if (response.status === 200) {

 loadEvents();

 } else {

 alert(response.message);

 }

 } catch (error) {

 }

 };



/**
 * ADD ZOOM LINK
 */
const handleAddZoom = async (event) => {

 try {

 if (!window.confirm("Generate Zoom link for this event?")) return;

 const safeGenerateZoom =
 generateZoomLink ||
 AdminEventService.generateZoomLink;

 if (!safeGenerateZoom) {

 alert("generateZoomLink function not found");
 return;

 }

 const response = await safeGenerateZoom(event.id);

 if (response.status === 201) {

 alert("Zoom link created successfully");

 loadEvents();

 } 
 else if (response.status === 200) {

 alert("Zoom link already exists");

 loadEvents();

 } 
 else {

 alert(response.message);

 }

 } catch (error) {

 alert("Failed to generate Zoom link");

 }

};



 return (

 <div className="admin-container enhanced-admin">

 {/* Analytics Dashboard Header */}
 <div className="analytics-header">
 <div className="analytics-cards">
 <div className="analytics-card">
 <div className="card-icon">📊</div>
 <div className="card-content">
 <div className="card-value">{analytics.totalEvents}</div>
 <div className="card-label">Total Events</div>
 </div>
 </div>
 
 <div className="analytics-card">
 <div className="card-icon">🎫</div>
 <div className="card-content">
 <div className="card-value">{analytics.totalBookings}</div>
 <div className="card-label">Total Bookings</div>
 </div>
 </div>
 
 <div className="analytics-card">
 <div className="card-icon">💰</div>
 <div className="card-content">
 <div className="card-value">₹{analytics.totalRevenue.toLocaleString()}</div>
 <div className="card-label">Total Revenue</div>
 </div>
 </div>
 
 <div className="analytics-card">
 <div className="card-icon">📈</div>
 <div className="card-content">
 <div className="card-value">{analytics.avgOccupancy}%</div>
 <div className="card-label">Avg Occupancy</div>
 </div>
 </div>
 </div>
 </div>

 <div className="admin-header">

 <h2>Event Management</h2>

 <button className="btn primary create-event-btn" onClick={() => {

 setEditing(null);

 setShowForm(true);

 }}>

 <span className="btn-icon">➕</span> New Event

 </button>

 </div>



 {showForm && (

 <AdminEventForm

 event={editing}

 onSaved={() => {

 setShowForm(false);

 loadEvents();

 }}

 onCancel={() => {

 setShowForm(false);

 }}

 />

 )}



 {loading ? (

 <p>Loading events...</p>

 ) : (

 <table className="event-table enhanced-event-table">

 <thead>

 <tr>

 <th>Event Details</th>
 <th>Schedule</th>
 <th>Speaker</th>
 <th>Pricing</th>
 <th>Capacity</th>
 <th>Status</th>
 <th>Actions</th>

 </tr>

 </thead>

 <tbody>

 {events.length === 0 ? (

 <tr>

 <td colSpan="7" className="empty-state">
 <div className="empty-content">
 <span className="empty-icon">📅</span>
 <p>No events found</p>
 <button onClick={() => {
 setEditing(null);
 setShowForm(true);
 }} className="btn primary">
 Create First Event
 </button>
 </div>
 </td>

 </tr>

 ) : (

 events.map((e) => {
 const occupancyRate = e.maxParticipants > 0 ? 
 ((e.currentBookings || 0) / e.maxParticipants * 100).toFixed(1) : 0;
 const revenue = (e.currentBookings || 0) * (e.price || 0);
 
 return (
 <tr key={e.id} className={e.cancelled ? 'cancelled-event' : ''}>

 <td className="event-details-cell">
 <div className="event-title">{e.title}</div>
 <div className="event-meta">
 <span className="category-tag">{e.category || 'Workshop'}</span>
 <span className="level-tag">{e.level || 'All Levels'}</span>
 </div>
 <div className="event-description">{e.description}</div>
 </td>

 <td className="schedule-cell">
 <div className="schedule-info">
 <div className="date-info">
 <strong>{formatDateSafe(e.eventStartTime)}</strong>
 </div>
 <div className="time-info">
 {formatTimeSafe(e.eventStartTime)}
 </div>
 <div className="duration-info">
 {e.eventDuration} mins • {e.platform || 'Zoom'}
 </div>
 </div>
 </td>

 <td className="instructor-cell">
 <div className="instructor-info">
 <div className="instructor-name">{e.speakerName || 'TBD'}</div>
 {e.speakerBio && (
 <div className="instructor-rating">{e.speakerBio}</div>
 )}
 <div className="language-info">{e.language || 'English'}</div>
 </div>
 </td>

 <td className="pricing-cell">
 <div className="pricing-info">
 {e.originalPrice && e.originalPrice !== e.price && (
 <div className="original-price">₹{e.originalPrice}</div>
 )}
 <div className="current-price">₹{e.price || 0}</div>
 <div className="revenue-info">
 Revenue: ₹{revenue.toLocaleString()}
 </div>
 </div>
 </td>

 <td className="capacity-cell">
 <div className="capacity-info">
 <div className="booking-count">
 {e.currentBookings || 0}/{e.maxParticipants || 0}
 </div>
 <div className="occupancy-bar">
 <div 
 className="occupancy-fill" 
 style={{width: `${Math.min(occupancyRate, 100)}%`}}
 ></div>
 </div>
 <div className="occupancy-text">{occupancyRate}% filled</div>
 </div>
 </td>

 <td className="status-cell">
 {e.cancelled ? (
 <span className="status cancelled">Cancelled</span>
 ) : (
 <>
 <span className="status active">{e.status || 'Available'}</span>
 {e.currentBookings >= e.maxParticipants && (
 <span className="status full">Full</span>
 )}
 </>
 )}
 </td>

 <td className="actions-cell">
 <div className="action-buttons">

 <button 
 className="btn-icon edit" 
 onClick={() => {
 setEditing(e);
 setShowForm(true);
 }}
 title="Edit Event"
 >
 ✏️
 </button>

 {!e.cancelled && (
 <button
 className="btn-icon warning"
 onClick={() => handleCancel(e.id)}
 title="Cancel Event"
 >
 ❌
 </button>
 )}

 {e.zoomLink ? (
 <button
 className="btn-icon success"
 onClick={() => handleJoinZoom(e)}
 title="Join Zoom"
 >
 📹
 </button>
 ) : (
 <button
 className="btn-icon info"
 onClick={() => handleAddZoom(e)}
 title="Generate Zoom"
 >
 ➕
 </button>
 )}

 <button
 className="btn-icon info"
 onClick={() => navigate(`/admin/admin-events/${e.id}/users`, {
 state: { eventTitle: e.title }
 })}
 title="View Bookings"
 >
 👥
 </button>

 <button
 className="btn-icon danger"
 onClick={() => handleDelete(e.id)}
 title="Delete Event"
 >
 🗑️
 </button>

 </div>
 </td>

 </tr>
 );
 })

 )}

 </tbody>

 </table>

 )}

 </div>

 );

};


export default AdminEventPage;